package org.example.project.enginekalender

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import org.example.project.dashboard.DashboardTheme
import org.example.project.enginetabular.CellColor
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.events.MouseEvent
import org.w3c.dom.TouchEvent
import kotlin.math.roundToInt

internal object KalenderWeekOverviewStyles : StyleSheet() {
    val overviewRoot by style {
        self style {
            property("--kalender-grid-bg", "rgba(255,255,255,0.035)")
            property("--kalender-grid-bg-soft", "rgba(255,255,255,0.025)")
            property("--kalender-grid-line", "rgba(255,255,255,0.14)")
            property("--kalender-grid-line-soft", "rgba(255,255,255,0.10)")
            property("--kalender-grid-accent", "rgba(255,255,255,0.12)")
        }
        self + " table" style {
            property("border-collapse", "separate")
            property("border-spacing", "0")
            backgroundColor(Color.transparent)
        }
        self + " thead" style {
            backgroundColor(Color("rgba(255,255,255,0.025)"))
        }
        self + " th" style {
            property("background", "rgba(255,255,255,0.025)")
            color(DashboardTheme.TEXT_LIGHT)
            property("border-right", "1px solid rgba(255,255,255,0.14)")
            property("border-bottom", "1px solid rgba(255,255,255,0.14)")
            borderRadius(0.px)
            fontSize(13.px)
            fontWeight("800")
            height(50.px)
            property("text-transform", "none")
        }
        self + " td" style {
            property("background", "transparent")
            color(DashboardTheme.TEXT_LIGHT)
            property("border-right", "1px solid rgba(255,255,255,0.10)")
            property("border-bottom", "1px solid rgba(255,255,255,0.10)")
            borderRadius(0.px)
            height(54.px)
            fontSize(12.px)
            fontWeight("700")
        }
        self + " tbody tr:hover td" style {
            property("background", "rgba(255,255,255,0.030)")
        }
        self + " td:hover" style {
            property("background", "rgba(255,255,255,0.055)")
        }
    }

    val gridFrame by style {
        backgroundColor(Color("rgba(255,255,255,0.035)"))
        borderRadius(8.px)
        property("box-shadow", "none")
        self style {
            property("scrollbar-width", "none")
            property("-ms-overflow-style", "none")
        }
        self + "::-webkit-scrollbar" style {
            display(DisplayStyle.None)
        }
    }
}

@Composable
fun KalenderWeekDetail(
    selectedDate: String,
    art: String,
    kalenderUserId: String,
    kalenderFirmenId: String = "",
    ansicht: String = "Woche",
    serviceBookingMode: Boolean = false,
    showPrivateBookingDetails: Boolean = true,
    canManageSlotSettings: Boolean = true,
    fillVisibleHours: Boolean = false,
    bookingFilter: (All) -> Boolean = { true },
    slotTimeOverride: Pair<String, String>? = null,
    selectedBookingCell: Pair<String, Int>?,
    onSelectedBookingCellChange: (Pair<String, Int>) -> Unit,
    bookingRequestToken: Int,
    settingsRequestToken: Int,
    bookingTemplate: All? = null,
    onBookingSaved: ((All) -> Unit)? = null,
    onNavigateWeek: (Int) -> Unit = {}
) {
    Style(KalenderWeekOverviewStyles)
    val kalenderLiveVersion = GlobalManager.jsonDomainVersion("kalender")
    val detailData = remember(selectedDate) { buildKalenderWeekDetailData(selectedDate) }
    val scope = rememberCoroutineScope()
    val currentUser = LoginService.getUser()
    val bookings = remember { mutableStateListOf<All>() }
    var scrollFrameRef by remember { mutableStateOf<HTMLDivElement?>(null) }
    var weekSwipeStart by remember(selectedDate, ansicht) { mutableStateOf<Pair<Double, Double>?>(null) }
    var currentTimeTick by remember { mutableStateOf(0.0) }
    var bookingPopup by remember { mutableStateOf<All?>(null) }
    var contextBookingId by remember { mutableStateOf<String?>(null) }
    var contextSaving by remember { mutableStateOf(false) }
    var contextSaveError by remember { mutableStateOf("") }
    fun prepareNewBooking(base: All): All {
        val template = bookingTemplate ?: return base
        val id = js("crypto.randomUUID()") as String
        contextBookingId = id
        return base.copy(id = id, bezeichnung = template.bezeichnung, beschreibung = template.beschreibung,
            akquiseProspectId = template.akquiseProspectId, firma = template.firma, ansprechpartner = template.ansprechpartner,
            email = template.email, telefon = template.telefon, ort = "Online", terminArt = template.terminArt)
    }
    var clickedBooking by remember { mutableStateOf<All?>(null) }
    var hoveredBooking by remember { mutableStateOf<All?>(null) }
    var hoverClientX by remember { mutableStateOf(0.0) }
    var hoverClientY by remember { mutableStateOf(0.0) }
    var clickClientX by remember { mutableStateOf(0.0) }
    var clickClientY by remember { mutableStateOf(0.0) }
    var allowedBookingSlots by remember { mutableStateOf("08:00" to "18:00") }
    var activeWeekdays by remember { mutableStateOf(setOf("mo", "di", "mi", "do", "fr", "sa", "so")) }
    var settingsPopupOpen by remember { mutableStateOf(false) }
    val currentHourSlot = remember(currentTimeTick) {
        val now = js("new Date()")
        ((now.getHours() as Int).coerceIn(0, 23))
    }
    val filteredDetailData = remember(detailData, selectedDate, ansicht) {
        val filteredDays = if (ansicht == "Tag") {
            detailData.module.filter { it.datum == selectedDate }
        } else {
            detailData.module
        }
        detailData.copy(module = filteredDays)
    }
    val effectiveBookingSlots = slotTimeOverride ?: allowedBookingSlots
    val visibleHours = remember(filteredDetailData, effectiveBookingSlots) {
        visibleKalenderHours(filteredDetailData, effectiveBookingSlots.first, effectiveBookingSlots.second)
    }
    LaunchedEffect(Unit) {
        while (true) {
            currentTimeTick = js("Date.now()") as Double
            delay(30_000)
        }
    }

    val cellBackgroundColors =
        buildBookingCellColors(bookings, filteredDetailData, visibleHours, effectiveBookingSlots.first, effectiveBookingSlots.second)
    val currentUserId = LoginService.getId()
    val currentFirmenId = currentUser?.firmenId.orEmpty().trim().ifBlank { GlobalManager.currentFirmenId.trim() }

    LaunchedEffect(art, kalenderUserId, kalenderFirmenId, kalenderLiveVersion) {
        bookings.clear()
        val loadedBookings =
            if (kalenderFirmenId.isNotBlank()) loadScopedKalenderBookings(kalenderFirmenId, kalenderUserId)
            else loadKalenderBookings(art, kalenderUserId)
        bookings.addAll(loadedBookings.filter(bookingFilter))
        val slotSettings =
            if (kalenderFirmenId.isNotBlank()) loadScopedKalenderSlotSettings(kalenderFirmenId, kalenderUserId)
            else loadKalenderSlotSettings(art, kalenderUserId)
        allowedBookingSlots = slotSettings.first to slotSettings.second
        activeWeekdays = slotSettings.third
    }

    LaunchedEffect(filteredDetailData, selectedDate, effectiveBookingSlots) {
        if (filteredDetailData.module.isEmpty()) return@LaunchedEffect
        val invalidSelection = selectedBookingCell?.second?.let { it !in filteredDetailData.module.indices } ?: true
        if (invalidSelection) {
            onSelectedBookingCellChange(defaultBookingCell(filteredDetailData, selectedDate, effectiveBookingSlots.first))
        }
    }

    LaunchedEffect(bookingRequestToken, filteredDetailData, kalenderUserId, effectiveBookingSlots) {
        if (bookingRequestToken <= 0) return@LaunchedEffect
        val baseCell = selectedBookingCell ?: defaultBookingCell(filteredDetailData, selectedDate, effectiveBookingSlots.first)
        val targetCell = coerceBookingCellToAllowedRange(filteredDetailData, baseCell, selectedDate, effectiveBookingSlots.first, effectiveBookingSlots.second)
        onSelectedBookingCellChange(targetCell)
        bookingPopup = prepareNewBooking(initialPopupState(filteredDetailData, targetCell.first, targetCell.second, currentUserId, kalenderUserId))
    }

    LaunchedEffect(settingsRequestToken) {
        if (settingsRequestToken <= 0 || !canManageSlotSettings) return@LaunchedEffect
        settingsPopupOpen = true
    }

    LaunchedEffect(filteredDetailData, currentHourSlot, effectiveBookingSlots) {
        if (filteredDetailData.module.none { it.aktiv }) return@LaunchedEffect
        val firstVisibleHour = visibleHours.firstOrNull() ?: 0
        val topHour = currentHourSlot.coerceIn(firstVisibleHour, visibleHours.lastOrNull() ?: firstVisibleHour)
        val rowIndex = visibleHours.indexOf(topHour).takeIf { it >= 0 } ?: 0
        scrollFrameRef?.scrollTop = (rowIndex * 4 * 32.0)
    }

    Div({
        classes(KalenderWeekOverviewStyles.overviewRoot)
        style {
            flexGrow(1)
            flexShrink(1)
            minWidth(0.px)
            minHeight(0.px)
            height(100.percent)
            padding(0.px)
            backgroundColor(Color.transparent)
            boxSizing("border-box")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("overflow", "hidden")
        }
        onMouseLeave { hoveredBooking = null }
        onTouchStart {
            if (ansicht != "Woche") return@onTouchStart
            val touch = it.touches.item(0) ?: return@onTouchStart
            weekSwipeStart = touch.clientX.toDouble() to touch.clientY.toDouble()
        }
        onTouchEnd {
            if (ansicht != "Woche") return@onTouchEnd
            val start = weekSwipeStart ?: return@onTouchEnd
            weekSwipeStart = null
            val touchEvent = it.nativeEvent as? TouchEvent ?: return@onTouchEnd
            val touch = touchEvent.changedTouches.item(0) ?: return@onTouchEnd
            kalenderWeekSwipeDirection(start.first, start.second, touch.clientX.toDouble(), touch.clientY.toDouble())
                ?.let(onNavigateWeek)
        }
    }) {
        Div({
            classes(KalenderWeekOverviewStyles.gridFrame)
            ref { scrollFrameRef = it; onDispose { scrollFrameRef = null } }
            style {
                width(100.percent)
                height(100.percent)
                maxWidth(100.percent)
                maxHeight(100.percent)
                overflow("auto")
                property("border", "1px solid rgba(255,255,255,0.14)")
                borderRadius(8.px)
                property("background-clip", "padding-box")
                backgroundColor(Color("#141820"))
                boxSizing("border-box")
            }
        }) {
            KalenderNativeWeekGrid(
                detailData = filteredDetailData,
                visibleHours = visibleHours,
                allowedFrom = effectiveBookingSlots.first,
                allowedTo = effectiveBookingSlots.second,
                bookings = bookings,
                cellBackgroundColors = cellBackgroundColors,
                activeWeekdays = activeWeekdays,
                fillVisibleHours = fillVisibleHours,
                selectedBookingCell = selectedBookingCell,
                onSelectedBookingCellChange = onSelectedBookingCellChange,
                onEmptyCellClick = { rowId, colIndex ->
                    onSelectedBookingCellChange(rowId to colIndex)
                    bookingPopup = prepareNewBooking(initialPopupState(
                        filteredDetailData,
                        rowId,
                        colIndex,
                        currentUserId,
                        kalenderUserId
                    ))
                },
                onBookingClick = { booking, clientX, clientY ->
                    clickedBooking = booking
                    clickClientX = clientX
                    clickClientY = clientY
                },
                onBookingHover = { booking, clientX, clientY ->
                    hoveredBooking = booking
                    hoverClientX = clientX
                    hoverClientY = clientY
                },
                onBookingHoverLeave = { booking ->
                    if (hoveredBooking?.id == booking?.id) hoveredBooking = null
                },
                showPrivateBookingDetails = showPrivateBookingDetails
            )
        }

        hoveredBooking?.let { booking ->
            BookingHoverCard(
                booking = booking,
                clientX = hoverClientX,
                clientY = hoverClientY,
                interactive = false,
                showDetails = showBookingDetails(booking, showPrivateBookingDetails, currentFirmenId, currentUserId)
            )
        }

        clickedBooking?.let { booking ->
            BookingHoverCard(
                booking = booking,
                clientX = clickClientX,
                clientY = clickClientY,
                interactive = true,
                showDetails = showBookingDetails(booking, showPrivateBookingDetails, currentFirmenId, currentUserId),
                onClose = { clickedBooking = null },
                onEdit = if (showBookingDetails(booking, showPrivateBookingDetails, currentFirmenId, currentUserId) &&
                    canEditServiceBooking(booking, currentFirmenId, currentUserId)
                ) {
                    {
                    bookingPopup = booking
                    clickedBooking = null
                    }
                } else null,
                onDelete = if (showBookingDetails(booking, showPrivateBookingDetails, currentFirmenId, currentUserId) &&
                    canEditServiceBooking(booking, currentFirmenId, currentUserId)
                ) {
                    {
                    clickedBooking = null
                    bookings.removeAll { it.id == booking.id }
                    scope.launch {
                        deleteKalenderBooking(art, kalenderUserId, booking)
                        bookings.clear()
                        val loadedBookings =
                            if (kalenderFirmenId.isNotBlank()) loadScopedKalenderBookings(kalenderFirmenId, kalenderUserId)
                            else loadKalenderBookings(art, kalenderUserId)
                        bookings.addAll(loadedBookings.filter(bookingFilter))
                    }
                    }
                } else null,
                onConfirm = if (isProviderServiceBooking(booking, currentFirmenId, currentUserId) &&
                    booking.zustand == ZustandManager.Termin.ANGEMELDET
                ) ({
                    clickedBooking = null
                    scope.launch {
                        persistServiceKalenderBooking(
                            booking.anbieterFirmenId,
                            booking.anbieterUserId,
                            booking.copy(zustand = ZustandManager.Termin.ZUGESAGT)
                        )
                        bookings.clear()
                        val loadedBookings =
                            if (kalenderFirmenId.isNotBlank()) loadScopedKalenderBookings(kalenderFirmenId, kalenderUserId)
                            else loadKalenderBookings(art, kalenderUserId)
                        bookings.addAll(loadedBookings.filter(bookingFilter))
                    }
                }) else null,
                onCancelBooking = if (isServiceBooking(booking) &&
                    canCancelServiceBooking(booking, currentFirmenId, currentUserId)
                ) ({
                    clickedBooking = null
                    scope.launch {
                        persistServiceKalenderBooking(
                            booking.anbieterFirmenId,
                            booking.anbieterUserId,
                            booking.copy(zustand = ZustandManager.Termin.STORNIERT)
                        )
                        bookings.clear()
                        val loadedBookings =
                            if (kalenderFirmenId.isNotBlank()) loadScopedKalenderBookings(kalenderFirmenId, kalenderUserId)
                            else loadKalenderBookings(art, kalenderUserId)
                        bookings.addAll(loadedBookings.filter(bookingFilter))
                    }
                }) else null,
                onOpenTraining = if (isTrainingBooking(booking)) {
                    {
                        val baseHash = window.location.hash.substringBefore("?").ifBlank { "#/dashboard" }
                        window.location.hash = "$baseHash?screen=SchulungsFlowScreen&schulungId=${booking.schulungId}"
                    }
                } else null
            )
        }
    }

    bookingPopup?.let { popup ->
        TerminBookingPopup(
            popupState = popup,
            art = art,
            serviceBookingMode = serviceBookingMode,
            providerFirmenId = kalenderFirmenId,
            providerUserId = kalenderUserId,
            allowedFrom = allowedBookingSlots.first,
            allowedTo = allowedBookingSlots.second,
            blockedBookings = bookings.toList(),
            showBlockedBookingDetails = showPrivateBookingDetails,
            onClose = { if (!contextSaving) bookingPopup = null },
            saving = contextSaving,
            saveError = contextSaveError,
            onSave = { booking ->
                if (!contextSaving) scope.launch {
                    contextSaving = true
                    try {
                    val providerFirmenId = booking.anbieterFirmenId.ifBlank { kalenderFirmenId }
                    val providerUserId = booking.anbieterUserId.ifBlank { kalenderUserId }
                    val persisted = if ((serviceBookingMode ||
                            isServiceBooking(booking) ||
                            (booking.anbieterFirmenId.isNotBlank() &&
                                booking.anbieterUserId.isNotBlank())) &&
                        providerFirmenId.isNotBlank() &&
                        providerUserId.isNotBlank()
                    ) {
                        persistServiceKalenderBooking(providerFirmenId, providerUserId, booking)
                    } else {
                        persistKalenderBooking(
                            art = art,
                            booking = booking
                        )
                    }
                    if (booking.akquiseProspectId.isNotBlank()) {
                        val confirmed = persisted.firstOrNull { it.id == booking.id }
                        if (confirmed == null) {
                            contextSaveError = "Kalenderbuchung wurde nicht bestätigt. Bitte erneut speichern."
                            return@launch
                        }
                        contextSaveError = ""
                        if (booking.id == contextBookingId) {
                            onBookingSaved?.invoke(confirmed)
                            contextBookingId = null
                        }
                    }
                    bookings.clear()
                    val loadedBookings =
                        if (kalenderFirmenId.isNotBlank()) loadScopedKalenderBookings(kalenderFirmenId, kalenderUserId)
                        else loadKalenderBookings(art, kalenderUserId)
                    bookings.addAll(loadedBookings.filter(bookingFilter))
                    bookingPopup = null
                    } catch (e: Exception) {
                        contextSaveError = e.message ?: "Kalenderbuchung fehlgeschlagen."
                    } finally { contextSaving = false }
                }
            }
        )
    }

    if (settingsPopupOpen && canManageSlotSettings) {
        TerminSlotSettingsPopup(
            initialFrom = allowedBookingSlots.first,
            initialTo = allowedBookingSlots.second,
            initialActiveWeekdays = activeWeekdays,
            onClose = { settingsPopupOpen = false },
            onSave = { from, to, weekdays ->
                allowedBookingSlots = from to to
                activeWeekdays = weekdays
                settingsPopupOpen = false
                scope.launch {
                    if (kalenderFirmenId.isNotBlank()) {
                        saveScopedKalenderSlotSettings(kalenderFirmenId, kalenderUserId, from, to, weekdays)
                    } else {
                        saveKalenderSlotSettings(art, kalenderUserId, from, to, weekdays)
                    }
                }
            }
        )
    }
}

@Composable
internal fun KalenderNativeWeekGrid(
    detailData: All,
    visibleHours: List<Int>,
    allowedFrom: String = "00:00",
    allowedTo: String = "24:00",
    bookings: List<All>,
    cellBackgroundColors: Map<Pair<String, Int>, CellColor>,
    activeWeekdays: Set<String>,
    fillVisibleHours: Boolean,
    selectedBookingCell: Pair<String, Int>?,
    onSelectedBookingCellChange: (Pair<String, Int>) -> Unit,
    onEmptyCellClick: (String, Int) -> Unit,
    onBookingClick: (All, Double, Double) -> Unit,
    onBookingHover: (All, Double, Double) -> Unit,
    onBookingHoverLeave: (All?) -> Unit,
    showPrivateBookingDetails: Boolean,
    taskMode: Boolean = false,
    markedTaskDates: Set<String> = emptySet(),
    taskCellContent: @Composable (String, Int) -> Unit = { _, _ -> }
) {
    val days = detailData.module
    val visibleSlots = quarterSlots(visibleHours, allowedFrom, allowedTo)
    val columnsCss = "70px repeat(${days.size.coerceAtLeast(1)}, minmax(0, 1fr))"
    val bodyRowsCss = "repeat(${visibleSlots.size.coerceAtLeast(1)}, minmax(32px, 1fr))"

    Div({
        style {
            minWidth(0.px)
            width(100.percent)
            display(DisplayStyle.Grid)
            gridTemplateColumns(columnsCss)
            position(Position.Sticky)
            top(0.px)
            property("z-index", "8")
            property("background", "#141820")
            property("border-bottom", "1px solid rgba(255,255,255,0.14)")
            property("box-shadow", "0 1px 0 rgba(255,255,255,0.06)")
        }
    }) {
        Div({ style { padding(12.px, 10.px); boxSizing("border-box") } }) {
            Span({
                style {
                    color(DashboardTheme.TEXT_MUTED)
                    fontSize(12.px)
                    fontWeight("700")
                }
            }) { Text("Zeit") }
        }
        days.forEach { day ->
            val dayIsBookable = normalizedWeekdayLabel(day.label) in activeWeekdays
            val dayState = kalenderDayState(bookings.filter { it.datum == day.datum })
            val taskCount = if (taskMode) bookings.count { it.datum == day.datum } else 0
            val vacationBookings = dayState.bookings.filter(::kalenderBookingIsVacation)
            val timedBookingCount = dayState.count - vacationBookings.size
            val isSelected = selectedBookingCell?.second?.let { days.getOrNull(it)?.datum == day.datum } == true
            Div({
                style {
                    minWidth(0.px)
                    padding(12.px, 10.px)
                    property("border-left", "1px solid rgba(255,255,255,0.14)")
                    backgroundColor(
                        when {
                            !dayIsBookable -> Color("rgba(255,255,255,0.020)")
                            day.datum in markedTaskDates -> Color("rgba(87,145,225,0.35)")
                            isSelected || day.aktiv -> Color("rgba(255,255,255,0.055)")
                            else -> Color.transparent
                        }
                    )
                    if (!dayIsBookable) opacity(0.54)
                    boxSizing("border-box")
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                    }
                }) {
                    Span({
                        style {
                            color(if (dayIsBookable) DashboardTheme.TEXT_LIGHT else DashboardTheme.TEXT_MUTED)
                            fontSize(13.px)
                            fontWeight("800")
                        }
                    }) { Text(day.label) }
                    Span({
                        style {
                            color(if (isSelected || day.aktiv) DashboardTheme.TEXT_LIGHT else DashboardTheme.TEXT_MUTED)
                            fontSize(20.px)
                            fontWeight("900")
                            if (isSelected || day.aktiv) {
                                property("text-shadow", "0 0 10px rgba(255,255,255,0.72)")
                            }
                        }
                    }) { Text(day.bezeichnung) }
                }
                if (taskMode || timedBookingCount > 0 || vacationBookings.isEmpty()) {
                    Span({
                        style {
                            color(DashboardTheme.TEXT_MUTED)
                            fontSize(11.px)
                            fontWeight("600")
                        }
                    }) { Text(if (taskMode) when (taskCount) {
                        0 -> "Keine Aufgaben"
                        1 -> "1 Aufgabe"
                        else -> "$taskCount Aufgaben"
                    } else kalenderBookingCountText(timedBookingCount)) }
                }
                if (!taskMode) vacationBookings.distinctBy { it.id.ifBlank { "${it.datumVon}_${it.datumBis}_${it.abwesenheitsart}_${it.zustand}" } }.forEach { vacation ->
                    val statusColor = kalenderDayState(listOf(vacation)).colorHex ?: return@forEach
                    Span({
                        style {
                            display(DisplayStyle.Block)
                            marginTop(4.px)
                            color(Color(statusColor))
                            fontSize(11.px)
                            fontWeight("800")
                        }
                    }) {
                        Text(
                            if (showPrivateBookingDetails) kalenderVacationRangeText(vacation)
                            else "Zeitbereich nicht frei"
                        )
                    }
                }
            }
        }
    }

    Div({
        style {
            minWidth(0.px)
            width(100.percent)
            display(DisplayStyle.Grid)
            if (fillVisibleHours) {
                property("grid-template-rows", bodyRowsCss)
                property("height", "calc(100% - 73px)")
            }
            minHeight(if (fillVisibleHours) (visibleSlots.size * 32).px else 0.px)
        }
    }) {
        visibleSlots.forEach { slotMinute ->
            val rowId = quarterTime(slotMinute)
            Div({
                style {
                    minWidth(0.px)
                    minHeight(0.px)
                    width(100.percent)
                    display(DisplayStyle.Grid)
                    gridTemplateColumns(columnsCss)
                    if (!fillVisibleHours) height(32.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        padding(0.px, 12.px)
                        color(DashboardTheme.TEXT_MUTED)
                        fontSize(12.px)
                        fontWeight("800")
                        property("border-right", "1px solid rgba(255,255,255,0.14)")
                        property("border-bottom", "1px solid rgba(255,255,255,0.10)")
                        boxSizing("border-box")
                    }
                }) { Text(rowId) }

                days.forEachIndexed { colIndex, day ->
                    val dayIsBookable = normalizedWeekdayLabel(day.label) in activeWeekdays
                    val occupied = if (taskMode) {
                        bookings.firstOrNull { occupiesSlot(it, day.datum, slotMinute) }
                    } else {
                        bookingForCell(bookings, detailData, rowId, colIndex)
                    }
                    val booking = if (taskMode) null else occupied
                    val nextMinute = slotMinute + 15
                    val continues = occupied != null && nextMinute in visibleSlots &&
                        (if (taskMode) {
                            bookings.firstOrNull { occupiesSlot(it, day.datum, nextMinute) }
                        } else {
                            bookingForCell(bookings, detailData, quarterTime(nextMinute), colIndex)
                        }) === occupied
                    val summary = if (taskMode) "" else bookingSummaryForCell(bookings, detailData, slotMinute, colIndex, showPrivateBookingDetails)
                    val color = cellBackgroundColors[rowId to colIndex]
                    val selected = dayIsBookable && selectedBookingCell == (rowId to colIndex)
                    Div({
                        style {
                            minWidth(0.px)
                            height(if (fillVisibleHours) 100.percent else 32.px)
                            minHeight(0.px)
                            padding(if (taskMode) 0.px else 4.px, 8.px)
                            property("border-right", "1px solid rgba(255,255,255,0.10)")
                            property("border-bottom", if (continues) "none" else "1px solid rgba(255,255,255,0.10)")
                            boxSizing("border-box")
                            cursor(if (dayIsBookable || booking != null) "pointer" else "not-allowed")
                            position(Position.Relative)
                            overflow(if (taskMode) "visible" else "hidden")
                            backgroundColor(
                                if (taskMode && occupied != null) Color("#303a4c")
                                else if (color?.color != null) color.color
                                else if (dayIsBookable) Color.transparent
                                else Color("rgba(255,255,255,0.018)")
                            )
                            if (!dayIsBookable) opacity(0.48)
                            if (selected) property("box-shadow", "inset 0 0 0 2px rgba(255,255,255,0.42)")
                            if (!color?.outlineCss.isNullOrBlank()) property("box-shadow", color!!.outlineCss)
                        }
                        onClick { event ->
                            val mouseEvent = event.nativeEvent as? MouseEvent
                            val clientX = mouseEvent?.clientX?.toDouble() ?: 0.0
                            val clientY = mouseEvent?.clientY?.toDouble() ?: 0.0
                            if (taskMode) {
                                if (dayIsBookable) onEmptyCellClick(rowId, colIndex)
                            } else if (booking != null) {
                                onBookingClick(booking, clientX, clientY)
                            } else if (!dayIsBookable) {
                                return@onClick
                            } else {
                                onEmptyCellClick(rowId, colIndex)
                            }
                        }
                        onDoubleClick { event ->
                            if (booking != null) {
                                val mouseEvent = event.nativeEvent as? MouseEvent
                                onBookingClick(
                                    booking,
                                    mouseEvent?.clientX?.toDouble() ?: 0.0,
                                    mouseEvent?.clientY?.toDouble() ?: 0.0
                                )
                            }
                        }
                        onMouseEnter { event ->
                            if (booking != null) {
                                val mouseEvent = event.nativeEvent as? MouseEvent
                                onBookingHover(
                                    booking,
                                    mouseEvent?.clientX?.toDouble() ?: 0.0,
                                    mouseEvent?.clientY?.toDouble() ?: 0.0
                                )
                            }
                        }
                        onMouseMove { event ->
                            if (booking != null) {
                                val mouseEvent = event.nativeEvent as? MouseEvent
                                onBookingHover(
                                    booking,
                                    mouseEvent?.clientX?.toDouble() ?: 0.0,
                                    mouseEvent?.clientY?.toDouble() ?: 0.0
                                )
                            }
                        }
                        onMouseLeave { onBookingHoverLeave(booking) }
                    }) {
                        if (taskMode) taskCellContent(day.datum, slotMinute)
                        if (!taskMode && summary.isNotBlank()) {
                            Span({
                                style {
                                    display(DisplayStyle.Block)
                                    color(DashboardTheme.TEXT_LIGHT)
                                    fontSize(12.px)
                                    fontWeight("800")
                                    lineHeight("1.2")
                                    property("white-space", "nowrap")
                                    overflow("hidden")
                                    property("text-overflow", "ellipsis")
                                }
                            }) { Text(summary) }
                            if (booking != null) {
                                Span({
                                    style {
                                        display(DisplayStyle.Block)
                                        color(DashboardTheme.TEXT_MUTED)
                                        fontSize(11.px)
                                        fontWeight("600")
                                        lineHeight("1.2")
                                        property("white-space", "nowrap")
                                        overflow("hidden")
                                        property("text-overflow", "ellipsis")
                                    }
                                }) { Text(booking.ort.ifBlank { "${terminVon(booking)} - ${terminBis(booking)}" }) }
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun kalenderBookingCountText(count: Int): String =
    when (count) {
        0 -> "Keine Termine"
        1 -> "1 Termin"
        else -> "$count Termine"
    }

private fun kalenderVacationRangeText(vacation: All): String {
    val art = vacation.abwesenheitsart.ifBlank { vacation.bezeichnung.ifBlank { "Urlaub" } }
    val von = vacation.datumVon.ifBlank { vacation.datum }
    val bis = vacation.datumBis.ifBlank { von }
    return "$art von ${kalenderFullDateLabel(von)} bis ${kalenderFullDateLabel(bis)}"
}

private fun kalenderFullDateLabel(iso: String): String {
    val parts = iso.split("-")
    if (parts.size != 3) return iso
    return "${parts[2]}.${parts[1]}.${parts[0]}"
}

private fun isServiceBooking(booking: All): Boolean =
    booking.serviceTermin

private fun isTrainingBooking(booking: All): Boolean =
    booking.schulungFlow &&
        booking.schulungsModus.equals("KI", ignoreCase = true)

private fun isServiceCreatorBooking(booking: All, currentFirmenId: String, currentUserId: String): Boolean =
    isServiceBooking(booking) &&
        (
            (booking.erstellerFirmenId == currentFirmenId &&
                booking.erstellerUserId == currentUserId) ||
            (booking.kundeFirmenId == currentFirmenId &&
                booking.kundeUserId == currentUserId)
        )

private fun isProviderServiceBooking(booking: All, currentFirmenId: String, currentUserId: String): Boolean =
    isServiceBooking(booking) &&
        booking.anbieterFirmenId == currentFirmenId &&
        booking.anbieterUserId == currentUserId

private fun isCustomerServiceBooking(booking: All, currentFirmenId: String, currentUserId: String): Boolean =
    isServiceBooking(booking) &&
        booking.kundeFirmenId == currentFirmenId &&
        booking.kundeUserId == currentUserId

private fun canCancelServiceBooking(booking: All, currentFirmenId: String, currentUserId: String): Boolean =
    booking.zustand != ZustandManager.Termin.STORNIERT &&
        booking.zustand != ZustandManager.Termin.STATTGEFUNDEN &&
        (isProviderServiceBooking(booking, currentFirmenId, currentUserId) ||
            isCustomerServiceBooking(booking, currentFirmenId, currentUserId))

private fun canEditServiceBooking(booking: All, currentFirmenId: String, currentUserId: String): Boolean =
    !isServiceBooking(booking) || isServiceCreatorBooking(booking, currentFirmenId, currentUserId)

private fun showBookingDetails(
    booking: All,
    showPrivateBookingDetails: Boolean,
    currentFirmenId: String,
    currentUserId: String
): Boolean =
    showPrivateBookingDetails ||
        isServiceCreatorBooking(booking, currentFirmenId, currentUserId) ||
        isProviderServiceBooking(booking, currentFirmenId, currentUserId) ||
        isCustomerServiceBooking(booking, currentFirmenId, currentUserId)

private fun defaultBookingCell(detailData: All, selectedDate: String, allowedFrom: String = "08:00"): Pair<String, Int> {
    val colIndex = detailData.module.indexOfFirst { it.datum == selectedDate }
        .takeIf { it >= 0 }
        ?: detailData.module.indexOfFirst { it.aktiv }.takeIf { it >= 0 }
        ?: 0
    val fallbackMinute = ((parseHourMinute(allowedFrom) ?: 8 * 60) + 14) / 15 * 15
    val minute = if (selectedDate == kalenderTodayIsoDate()) {
        val now = js("new Date()")
        (((now.getHours() as Int) * 60 + (now.getMinutes() as Int) + 14) / 15 * 15)
            .coerceAtLeast(fallbackMinute).coerceAtMost(23 * 60 + 45)
    } else {
        fallbackMinute
    }
    return quarterTime(minute) to colIndex
}

private fun coerceBookingCellToAllowedRange(
    detailData: All,
    cell: Pair<String, Int>,
    selectedDate: String,
    allowedFrom: String,
    allowedTo: String
): Pair<String, Int> {
    val selectedMinute = parseHourMinute(cell.first) ?: cell.first.toIntOrNull()?.times(60) ?: 0
    val allowedStartMinutes = parseHourMinute(allowedFrom) ?: return defaultBookingCell(detailData, selectedDate, allowedFrom)
    val allowedEndMinutes = parseHourMinute(allowedTo) ?: return defaultBookingCell(detailData, selectedDate, allowedFrom)
    val allowedStart = (allowedStartMinutes + 14) / 15 * 15
    return if (selectedMinute >= allowedStart && selectedMinute + 15 <= allowedEndMinutes) {
        quarterTime(selectedMinute) to cell.second
    } else {
        quarterTime(allowedStart) to cell.second
    }
}

private fun bookingSummaryForCell(
    bookings: List<All>,
    detailData: All,
    slotMinute: Int,
    colIndex: Int,
    showPrivateBookingDetails: Boolean
): String {
    val dayIso = detailData.module.getOrNull(colIndex)?.datum.orEmpty()
    val booking = bookings.firstOrNull {
        !kalenderBookingIsVacation(it) &&
            kalenderDayState(listOf(it)).count > 0 &&
            occupiesSlot(it, dayIso, slotMinute)
    } ?: return ""
    if (!isBookingLabelSlot(booking, slotMinute)) return ""
    if (!showPrivateBookingDetails) return "Zeitbereich nicht frei"
    val titleLabel = booking.bezeichnung
        .ifBlank { booking.art }
        .ifBlank { booking.titel.orEmpty() }
    val ortLabel = booking.ort
    return listOf(titleLabel, ortLabel).filter { it.isNotBlank() }.joinToString(" | ")
}

private fun buildBookingCellColors(
    bookings: List<All>,
    detailData: All,
    visibleHours: List<Int>,
    allowedFrom: String,
    allowedTo: String
): Map<Pair<String, Int>, CellColor> {
    val result = mutableMapOf<Pair<String, Int>, CellColor>()
    val slots = quarterSlots(visibleHours, allowedFrom, allowedTo)
    bookings.forEach { booking ->
        val bookingColor = kalenderDayState(listOf(booking)).colorHex ?: return@forEach
        detailData.module.forEachIndexed { colIndex, day ->
            if (day.datum != booking.datum) return@forEachIndexed
            slots.forEach { slotMinute ->
                if (occupiesSlot(booking, day.datum, slotMinute)) {
                    result[quarterTime(slotMinute) to colIndex] = CellColor(
                        color = Color("color-mix(in srgb, $bookingColor 14%, transparent)"),
                        outlineCss = bookingBlockOutlineCss(booking, slotMinute)
                    )
                }
            }
        }
    }
    buildCurrentTimeMarker(detailData, slots)?.let { marker ->
        if (!result.containsKey(marker)) {
            result[marker] = CellColor(
                outlineCss = "inset 0 0 0 2px rgba(255,255,255,0.42)"
            )
        }
    }
    return result
}

private fun buildCurrentTimeMarker(detailData: All, visibleSlots: List<Int>): Pair<String, Int>? {
    val todayColumnIndex = detailData.module.indexOfFirst { it.aktiv }
    if (todayColumnIndex < 0) return null
    val now = js("new Date()")
    val currentMinute = ((now.getHours() as Int) * 60 + (now.getMinutes() as Int)) / 15 * 15
    if (currentMinute !in visibleSlots) return null
    val rowId = quarterTime(currentMinute)
    return rowId to todayColumnIndex
}

private fun bookingForCell(
    bookings: List<All>,
    detailData: All,
    rowId: String,
    colIndex: Int
): All? {
    val slotMinute = parseHourMinute(rowId) ?: return null
    val dayIso = detailData.module.getOrNull(colIndex)?.datum.orEmpty()
    return bookings.firstOrNull {
        !kalenderBookingIsVacation(it) &&
            kalenderDayState(listOf(it)).count > 0 &&
            occupiesSlot(it, dayIso, slotMinute)
    }
}

fun conflictingKalenderBooking(bookings: List<All>, candidate: All): All? {
    if (kalenderDayState(listOf(candidate)).count == 0) return null
    return bookings.firstOrNull { existing ->
        existing.id != candidate.id &&
            existing.datum == candidate.datum &&
            kalenderDayState(listOf(existing)).count > 0 &&
            overlaps(terminVon(existing), terminBis(existing), terminVon(candidate), terminBis(candidate))
    }
}

private fun isBookingLabelSlot(booking: All, slotMinute: Int): Boolean {
    val startMinutes = parseHourMinute(terminVon(booking)) ?: return false
    return slotMinute <= startMinutes && startMinutes < slotMinute + 15
}

private fun bookingBlockOutlineCss(booking: All, slotMinute: Int): String {
    val from = parseHourMinute(terminVon(booking)) ?: return ""
    val to = parseHourMinute(terminBis(booking)) ?: return ""
    val slotStart = slotMinute
    val slotEnd = slotStart + 15
    val isTop = from in slotStart until slotEnd
    val isBottom = to in (slotStart + 1)..slotEnd
    val borderColor = "rgba(255,255,255,0.26)"
    val shadows = buildList {
        add("inset 2px 0 0 0 $borderColor")
        add("inset -2px 0 0 0 $borderColor")
        if (isTop) add("inset 0 2px 0 0 $borderColor")
        if (isBottom) add("inset 0 -2px 0 0 $borderColor")
    }
    return shadows.joinToString(", ")
}

private fun occupiesSlot(booking: All, dayIso: String, slotMinute: Int): Boolean {
    if (booking.datum != dayIso) return false
    val from = parseHourMinute(terminVon(booking)) ?: return false
    val to = parseHourMinute(terminBis(booking)) ?: return false
    val slotStart = slotMinute
    val slotEnd = slotStart + 15
    return from < slotEnd && to > slotStart
}

fun overlaps(leftFrom: String, leftTo: String, rightFrom: String, rightTo: String): Boolean {
    val lFrom = parseHourMinute(leftFrom) ?: return false
    val lTo = parseHourMinute(leftTo) ?: return false
    val rFrom = parseHourMinute(rightFrom) ?: return false
    val rTo = parseHourMinute(rightTo) ?: return false
    return lFrom < rTo && rFrom < lTo
}

private fun terminVon(booking: All): String =
    booking.uhrzeitVon

private fun terminBis(booking: All): String =
    booking.uhrzeitBis

private fun quarterTime(minutes: Int): String =
    "${(minutes / 60).toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}"

private fun quarterSlots(visibleHours: List<Int>, allowedFrom: String, allowedTo: String): List<Int> {
    val from = parseHourMinute(allowedFrom) ?: 0
    val to = parseHourMinute(allowedTo) ?: 24 * 60
    return visibleHours.flatMap { hour -> (0..3).map { hour * 60 + it * 15 } }
        .filter { it >= from && it + 15 <= to }
}

fun parseHourMinute(value: String): Int? {
    val parts = value.split(":")
    if (parts.size != 2) return null
    val hour = parts[0].toIntOrNull() ?: return null
    val minute = parts[1].toIntOrNull() ?: return null
    val isValid = when {
        hour in 0..23 && minute in 0..59 -> true
        hour == 24 && minute == 0 -> true
        else -> false
    }
    if (!isValid) return null
    return hour * 60 + minute
}

fun isWithinAllowedBookingRange(von: String, bis: String, allowedFrom: String, allowedTo: String): Boolean {
    val bookingFrom = parseHourMinute(von) ?: return false
    val bookingTo = parseHourMinute(bis) ?: return false
    val rangeFrom = parseHourMinute(allowedFrom) ?: return false
    val rangeTo = parseHourMinute(allowedTo) ?: return false
    return bookingFrom >= rangeFrom && bookingTo <= rangeTo && bookingFrom < bookingTo
}

fun isQuarterHourRange(von: String, bis: String): Boolean {
    val from = parseHourMinute(von) ?: return false
    val to = parseHourMinute(bis) ?: return false
    return from % 15 == 0 && to % 15 == 0 && to - from >= 15
}

fun normalizedWeekdayLabel(label: String): String =
    label.trim().lowercase().take(2)

internal fun visibleKalenderHours(detailData: All, allowedFrom: String, allowedTo: String): List<Int> {
    val startHour = ((parseHourMinute(allowedFrom) ?: 0) / 60).coerceIn(0, 23)
    val endHour = (((parseHourMinute(allowedTo) ?: 24 * 60) - 1).coerceAtLeast(startHour * 60) / 60).coerceIn(0, 23)
    return detailData.tore.filter { it in startHour..endHour }
}

private fun initialPopupState(
    detailData: All,
    rowId: String,
    colIndex: Int,
    creatorUserId: String,
    kalenderUserId: String
): All {
    val dayIso = detailData.module.getOrNull(colIndex)?.datum.orEmpty()
    val startMinute = parseHourMinute(rowId) ?: rowId.toIntOrNull()?.times(60) ?: 0
    val endMinute = (startMinute + 15).coerceAtMost(24 * 60)
    return All(
        id = "",
        datum = dayIso,
        art = "Termin",
        ort = "",
        uhrzeitVon = quarterTime(startMinute),
        uhrzeitBis = quarterTime(endMinute),
        erstelltVon = creatorUserId,
        kontakt = kalenderUserId
    )
}

@Composable
private fun BookingHoverCard(
    booking: All,
    clientX: Double,
    clientY: Double,
    interactive: Boolean,
    showDetails: Boolean = true,
    onClose: (() -> Unit)? = null,
    onEdit: (() -> Unit)? = null,
    onDelete: (() -> Unit)? = null,
    onConfirm: (() -> Unit)? = null,
    onCancelBooking: (() -> Unit)? = null,
    onOpenTraining: (() -> Unit)? = null
) {
    val externalBooking = isExternalBooking(booking)
    val popupWidth = if (interactive) 520.0 else 360.0
    val popupHeight = when {
        interactive && externalBooking -> 520.0
        interactive -> 440.0
        externalBooking -> 340.0
        else -> 250.0
    }
    val anchorLeft = (clientX + 10.0)
        .coerceAtLeast(14.0)
        .coerceAtMost(window.innerWidth.toDouble() - popupWidth - 14.0)
    val anchorTop = (clientY + 18.0)
        .coerceAtLeast(14.0)
        .coerceAtMost(window.innerHeight.toDouble() - popupHeight - 14.0)

    Div({
        style {
            position(Position.Fixed)
            left(anchorLeft.roundToInt().px)
            top(anchorTop.roundToInt().px)
            minWidth(200.px)
            maxWidth(popupWidth.roundToInt().px)
            padding(if (interactive) 14.px else 12.px)
            boxSizing("border-box")
            property("z-index", if (interactive) "60" else "50")
            backgroundColor(Color("rgba(15,17,21,0.94)"))
            borderRadius(8.px)
            property("border", "1px solid rgba(255,255,255,0.16)")
            property("box-shadow", "0 28px 80px rgba(0,0,0,0.46)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
            if (!interactive) property("pointer-events", "none")
        }
    }) {
        if (!showDetails) {
            BookingHoverLine("Status", "Zeitbereich nicht frei")
            BookingHoverLine("Tag", formatGermanDate(booking.datum))
            BookingHoverLine("Von bis", "${terminVon(booking)} - ${terminBis(booking)}")
        } else if (externalBooking) {
            BookingHoverLine("Termin", "Externe Buchung")
            BookingHoverLine("Tag", formatGermanDate(booking.datum))
            BookingHoverLine("Von bis", "${terminVon(booking)} - ${terminBis(booking)}")
            BookingHoverLine("Firma", booking.firma.ifBlank { "-" })
            BookingHoverLine("Vorname", booking.vorname.ifBlank { "-" })
            BookingHoverLine("Nachname", booking.nachname.ifBlank { "-" })
            BookingHoverLine("E-Mail", booking.email.ifBlank { "-" })
            BookingHoverLine("Telefon", booking.telefon.ifBlank { "-" })
            BookingHoverLine(
                "Nachricht",
                booking.freitext
                    .ifBlank { booking.beschreibung.orEmpty() }
                    .ifBlank { "-" }
            )
        } else if (interactive) {
            BookingHoverLine("Titel", booking.bezeichnung.ifBlank { booking.art })
            BookingHoverLine("Zustand", kalenderBookingStatusText(booking))
            if (isServiceBooking(booking)) {
                BookingHoverLine("Terminart", booking.terminArt.ifBlank { "-" })
            }
            BookingHoverLine("Tag", formatGermanDate(booking.datum))
            BookingHoverLine("Von bis", "${terminVon(booking)} - ${terminBis(booking)}")
            BookingHoverLine("Ort", booking.ort.ifBlank { "-" })
            if (isServiceBooking(booking)) {
                BookingHoverLine("Auftraggeber", serviceCreatorCompanyLabel(booking))
                BookingHoverLine("Terminersteller", serviceCreatorPersonLabel(booking))
            }
            BookingHoverLine("Freitext", booking.beschreibung.orEmpty().ifBlank { "-" })
        } else {
            BookingHoverValue(booking.beschreibung.orEmpty().ifBlank { booking.bezeichnung.ifBlank { "-" } })
            BookingHoverValue(booking.ort.ifBlank { "-" })
            BookingHoverValue("${formatGermanDate(booking.datum)} | ${terminVon(booking)} - ${terminBis(booking)}")
            BookingHoverValue(kalenderBookingStatusText(booking))
        }

        if (interactive) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(8.px)
                    flexWrap(FlexWrap.Wrap)
                    paddingTop(4.px)
                }
            }) {
                onConfirm?.let {
                    LumaticaButton("Termin zusagen", onClick = it, variant = LumaticaButtonVariant.Action)
                }
                onCancelBooking?.let {
                    LumaticaButton("Stornieren", onClick = it, variant = LumaticaButtonVariant.Standard)
                }
                onOpenTraining?.let {
                    LumaticaButton("Zur Schulung", onClick = it, variant = LumaticaButtonVariant.Action)
                }
                onEdit?.let {
                    LumaticaButton("Bearbeiten", onClick = it, variant = LumaticaButtonVariant.Standard)
                }
                onDelete?.let {
                    LumaticaButton("Löschen", onClick = it, variant = LumaticaButtonVariant.Action)
                }
                onClose?.let {
                    LumaticaButton("Schließen", onClick = it, variant = LumaticaButtonVariant.Standard)
                }
            }
        }
    }
}

private fun serviceCreatorCompanyLabel(booking: All): String =
    booking.erstellerFirma
        .ifBlank { booking.kundeFirma }
        .ifBlank { booking.eingeladenVonFirma }
        .ifBlank { "-" }

private fun serviceCreatorPersonLabel(booking: All): String =
    booking.erstellerName
        .ifBlank { booking.eingeladenVonName }
        .ifBlank { booking.kundeName }
        .ifBlank { "-" }

private fun isExternalBooking(booking: All): Boolean =
    booking.art == "Externe Buchung" ||
        booking.bookingHtml ||
        booking.quelle == "booking_html"

@Composable
private fun BookingHoverLine(label: String, value: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.FlexStart)
            gap(8.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        Span({
            style {
                color(DashboardTheme.TEXT_MUTED)
                fontSize(12.px)
                fontWeight("600")
                property("white-space", "nowrap")
            }
        }) { Text("$label:") }
        Span({
            style {
                color(DashboardTheme.TEXT_LIGHT)
                fontSize(14.px)
                fontWeight("500")
            }
        }) { Text(value) }
    }
}

@Composable
private fun BookingHoverValue(value: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(4.px)
        }
    }) {
        Span({
            style {
                color(DashboardTheme.TEXT_LIGHT)
                fontSize(14.px)
                fontWeight("500")
            }
        }) { Text(value) }
    }
}
